import React, { useState, useEffect } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { supabase } from '../lib/supabaseClient'
import SectionNavPills from '../components/SectionNavPills'
import PromoBanner from '../components/PromoBanner'
import RegisteredUsersCounter from '../components/RegisteredUsersCounter'

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL

// Feature flag: temporarily hide the Financial Literacy teaser from the public
// homepage. The card markup is preserved in the JSX so we can re-enable it by
// flipping this flag back to `true` once the FG content is ready.
const SHOW_FG_TEASER = false

const Home = () => {
  const navigate = useNavigate()
  const { user, hasPaidAccess, accessLoading, isAdmin, adminChecked } = useAuth() // Add admin detection

  // Public sample tests (no auth) — loaded once on mount.
  const [sampleTests, setSampleTests] = useState([])
  useEffect(() => {
    let cancelled = false
    fetch(`${BACKEND_URL}/api/public/sample-tests`)
      .then(r => (r.ok ? r.json() : []))
      .then(data => { if (!cancelled && Array.isArray(data)) setSampleTests(data.slice(0, 2)) })
      .catch(() => { /* silent: section just won't render */ })
    return () => { cancelled = true }
  }, [])

  // Admin users should see admin-specific homepage or be redirected
  if (user && adminChecked && isAdmin) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100">
        <div className="container mx-auto px-4 py-16">
          <div className="max-w-2xl mx-auto">
            <div className="bg-white rounded-lg shadow-lg p-8 text-center">
              <div className="text-6xl mb-6">⚙️</div>
              <h1 className="text-3xl font-bold text-gray-900 mb-4">
                Ste prihlásený ako administrátor
              </h1>
              <p className="text-gray-600 mb-8">
                Máte prístup k administrátorskému panelu pre správu testov a obsahu.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button
                  onClick={() => navigate('/admin')}
                  className="px-8 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition"
                >
                  Prejsť do Admin panelu
                </button>
                <button
                  onClick={() => navigate('/dashboard')}
                  className="px-8 py-3 bg-gray-100 text-gray-700 rounded-lg font-semibold hover:bg-gray-200 transition"
                >
                  Zobraziť Dashboard
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-transparent">
      
      {/* Sticky Top Navigation Bar for Homepage Sections — desktop only.
          On mobile (<md) the global hamburger in <Header /> is the sole nav. */}
      <div className="hidden md:block sticky top-0 z-50 bg-white/90 backdrop-blur-md border-b border-slate-200 shadow-sm transition-all duration-200">
        <div className="container mx-auto px-4">
          <nav className="flex items-center justify-center gap-1.5 py-3 text-sm overflow-x-auto scrollbar-hide" aria-label="Navigácia po sekciách">
            <a href="#preco-t9" className="whitespace-nowrap inline-flex items-center px-3.5 py-1.5 rounded-full bg-slate-50 text-slate-700 ring-1 ring-slate-200 font-medium hover:bg-blue-50 hover:text-blue-700 hover:ring-blue-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 focus-visible:ring-offset-1 transition-colors duration-150">
              Prečo EduMisia?
            </a>
            <a href="#ako-funguje" className="whitespace-nowrap inline-flex items-center px-3.5 py-1.5 rounded-full bg-slate-50 text-slate-700 ring-1 ring-slate-200 font-medium hover:bg-blue-50 hover:text-blue-700 hover:ring-blue-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 focus-visible:ring-offset-1 transition-colors duration-150">
              Ako to funguje?
            </a>
            <a href="#co-ponukame" className="whitespace-nowrap inline-flex items-center px-3.5 py-1.5 rounded-full bg-slate-50 text-slate-700 ring-1 ring-slate-200 font-medium hover:bg-blue-50 hover:text-blue-700 hover:ring-blue-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 focus-visible:ring-offset-1 transition-colors duration-150">
              Čo ponúkame?
            </a>
            <a href="#cennik" className="whitespace-nowrap inline-flex items-center px-3.5 py-1.5 rounded-full bg-slate-50 text-slate-700 ring-1 ring-slate-200 font-medium hover:bg-blue-50 hover:text-blue-700 hover:ring-blue-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 focus-visible:ring-offset-1 transition-colors duration-150">
              Cenník
            </a>
            <a href="#faq" className="whitespace-nowrap inline-flex items-center px-3.5 py-1.5 rounded-full bg-slate-50 text-slate-700 ring-1 ring-slate-200 font-medium hover:bg-blue-50 hover:text-blue-700 hover:ring-blue-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 focus-visible:ring-offset-1 transition-colors duration-150">
              Časté otázky
            </a>
            <a href="#o-nas" className="whitespace-nowrap inline-flex items-center px-3.5 py-1.5 rounded-full bg-slate-50 text-slate-700 ring-1 ring-slate-200 font-medium hover:bg-blue-50 hover:text-blue-700 hover:ring-blue-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 focus-visible:ring-offset-1 transition-colors duration-150">
              O nás
            </a>
            <a href="/contact" className="whitespace-nowrap inline-flex items-center px-3.5 py-1.5 rounded-full bg-slate-50 text-slate-700 ring-1 ring-slate-200 font-medium hover:bg-blue-50 hover:text-blue-700 hover:ring-blue-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 focus-visible:ring-offset-1 transition-colors duration-150">
              Kontakt
            </a>
          </nav>
        </div>
      </div>

      {/* Mobile-only compact scrollable pill nav (<md). Sticky just below the global header so it stays available across all homepage sections, including Cenník. */}
      <SectionNavPills />

      {/* "Moje konto" quick access — visible only to authenticated
          (non-admin) users so they can jump to their personal area
          directly from the homepage without opening the account menu.
          Admin users are already redirected above and do not render this. */}
      {user && (
        <section
          className="container mx-auto px-4 pt-6 md:pt-8"
          aria-label="Moje konto"
          data-testid="home-my-account-section"
        >
          <div className="max-w-6xl lg:max-w-7xl mx-auto">
            <div className="rounded-2xl bg-white/95 backdrop-blur-sm border border-emerald-200 ring-1 ring-emerald-100 shadow-md shadow-emerald-100/40 p-4 sm:p-5">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div>
                  <h2 className="text-base sm:text-lg font-semibold text-gray-900 flex items-center gap-2">
                    <span className="w-8 h-8 rounded-full bg-emerald-100 text-emerald-700 inline-flex items-center justify-center">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                    </span>
                    Moje konto
                  </h2>
                  <p className="text-xs sm:text-sm text-gray-600 mt-1">
                    Pokračujte tam, kde ste skončili — vaše testy, výsledky a nastavenia účtu.
                  </p>
                </div>
                <div className="flex flex-wrap gap-2 sm:gap-3">
                  <Link
                    to="/dashboard"
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 transition shadow-sm"
                    data-testid="home-quick-my-tests"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m-3-8h.01M5 6h14M5 6a2 2 0 00-2 2v10a2 2 0 002 2h14a2 2 0 002-2V8a2 2 0 00-2-2" />
                    </svg>
                    Moje testy
                  </Link>
                  <Link
                    to="/my-results"
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-emerald-800 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 transition"
                    data-testid="home-quick-my-results"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                    Moje výsledky
                  </Link>
                  <Link
                    to="/account"
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 border border-gray-200 transition"
                    data-testid="home-quick-account"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                    Zmena hesla
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Hero Section - Using T9 Monitor Reference Image */}
      <div className="container mx-auto px-4 pt-10 md:pt-14 pb-4 md:pb-6">
        <div className="max-w-6xl lg:max-w-7xl mx-auto">
          
          {/* Unified hero surface — one large rounded landing block that
              holds the left callout, center hero card and right benefit
              panel as a single integrated zone (on lg+). On md/sm/xs the
              inner pieces stack naturally. */}
          <div className="mb-10 lg:rounded-[2rem] lg:bg-gradient-to-br lg:from-[#D9FDD3] lg:via-[#F4FBF2] lg:to-white lg:ring-1 lg:ring-slate-200/70 lg:shadow-lg lg:shadow-slate-200/40 lg:p-5 xl:p-6">

          {/* Two-Card Grid (collapses to single centered card when FG teaser is hidden) */}
          <div
            className={
              SHOW_FG_TEASER
                ? 'grid md:grid-cols-2 gap-8'
                : 'max-w-3xl lg:max-w-none mx-auto lg:grid lg:grid-cols-[12rem_minmax(0,1fr)_12rem] lg:gap-4 lg:items-start'
            }
          >

            {/* Hero callout — "Každá otázka je po teste VYSVETLENÁ."
                Visible from md upwards. On tablet (md..lg-1) it stacks above
                the hero card with a compact max-width; on lg+ it occupies the
                first column of the 3-column hero grid (callout · hero · panel). */}
            <aside
              className="hidden md:block md:max-w-xs lg:max-w-none lg:self-start lg:mt-20 mb-6 lg:mb-0"
              data-testid="hero-callout"
              aria-label="Každá otázka je po teste vysvetlená"
            >
              <div className="rounded-2xl bg-white/95 backdrop-blur-sm border border-emerald-100 ring-1 ring-emerald-50 shadow-md shadow-emerald-100/40 p-4 sm:p-5">
                <div className="flex items-center gap-2 mb-2.5">
                  <span aria-hidden="true" className="inline-flex w-8 h-8 rounded-lg bg-gradient-to-br from-amber-400 to-amber-500 text-white items-center justify-center shadow-sm ring-2 ring-amber-100">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 18.75h6m-6 0c0 .75.5 2.25 3 2.25s3-1.5 3-2.25M12 3a6 6 0 00-3.5 10.85V16a1 1 0 001 1h5a1 1 0 001-1v-2.15A6 6 0 0012 3z" />
                    </svg>
                  </span>
                  <span className="text-[10px] font-semibold uppercase tracking-wider text-emerald-700 bg-emerald-50 ring-1 ring-emerald-200 rounded-full px-2 py-0.5">
                    Naše know-how
                  </span>
                </div>
                <h3 className="text-sm font-extrabold leading-tight tracking-tight text-slate-900 mb-2.5">
                  KAŽDÁ OTÁZKA JE
                  <br />
                  PO TESTE{' '}
                  <span className="text-emerald-600">VYSVETLENÁ.</span>
                </h3>
                <ul className="space-y-1.5 text-xs text-slate-700">
                  <li className="flex items-start gap-1.5">
                    <svg aria-hidden="true" className="w-3.5 h-3.5 mt-0.5 text-emerald-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="leading-snug">Dieťa pochopí chybu</span>
                  </li>
                  <li className="flex items-start gap-1.5">
                    <svg aria-hidden="true" className="w-3.5 h-3.5 mt-0.5 text-emerald-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="leading-snug">Nejde len o testovanie, ale o učenie.</span>
                  </li>
                </ul>
              </div>
            </aside>
            
            {/* Card 1: Real HTML hero (no baked-in image text). */}
            <div
              className="relative rounded-2xl ring-1 ring-slate-200/60 bg-white p-6 md:p-8 flex flex-col justify-between animate-fadeIn overflow-hidden lg:shadow-sm"
              data-testid="hero-card"
            >
              {/* Decorative-only background accents */}
              <div
                aria-hidden="true"
                className="pointer-events-none absolute -top-12 -right-12 w-56 h-56 rounded-full bg-blue-100 opacity-60 blur-3xl"
              />
              <div
                aria-hidden="true"
                className="pointer-events-none absolute -bottom-16 -left-16 w-44 h-44 rounded-full bg-emerald-100 opacity-50 blur-3xl"
              />

              <div className="relative">
                <h1
                  className="font-extrabold tracking-tight mb-4 text-center"
                  data-testid="hero-headline"
                >
                  <span className="block text-sm sm:text-base lg:text-lg text-slate-700 mb-0.5">
                    PRIPRAVTE VAŠE DIEŤA NA
                  </span>
                  <span className="block text-3xl sm:text-4xl lg:text-5xl leading-[1] text-slate-900 mb-1">
                    MONITOR
                  </span>
                  <span className="block text-sm sm:text-base lg:text-lg text-slate-700 mt-1.5 mb-0.5">
                    TAK, ABY MU
                  </span>
                  <span className="block text-3xl sm:text-4xl lg:text-5xl leading-[1] text-emerald-600 mb-1">
                    ROZUMELO
                  </span>
                  <span className="block text-xs sm:text-sm lg:text-base text-slate-600 mt-1.5 tracking-wide">
                    – NIE LEN TIPOVALO
                  </span>
                </h1>

                {/* Value badges */}
                <ul className="flex flex-wrap justify-center gap-1.5 mb-7" data-testid="hero-badges">
                  <li className="inline-flex items-center px-2.5 py-1 rounded-full bg-blue-50 text-blue-700 text-[11px] sm:text-xs font-medium border border-blue-100 whitespace-nowrap">
                    Okamžité vyhodnotenie
                  </li>
                  <li className="inline-flex items-center px-2.5 py-1 rounded-full bg-blue-50 text-blue-700 text-[11px] sm:text-xs font-medium border border-blue-100 whitespace-nowrap">
                    Vysvetlenie každej otázky
                  </li>
                  <li className="inline-flex items-center px-2.5 py-1 rounded-full bg-blue-50 text-blue-700 text-[11px] sm:text-xs font-medium border border-blue-100 whitespace-nowrap">
                    Prehľad pokroku
                  </li>
                  <li className="inline-flex items-center px-2.5 py-1 rounded-full bg-blue-50 text-blue-700 text-[11px] sm:text-xs font-medium border border-blue-100 whitespace-nowrap">
                    Prístup do 31. mája
                  </li>
                </ul>

                {/* CTAs */}
                {!accessLoading && (
                  <div className="flex flex-col gap-3 mb-7">
                    <div className="flex flex-col sm:flex-row gap-3">
                      <button
                        onClick={() => navigate(user && hasPaidAccess ? '/dashboard' : '/login')}
                        className="flex-1 px-6 py-3.5 bg-gradient-to-b from-orange-500 to-orange-600 text-white rounded-xl font-bold text-base shadow-lg shadow-orange-500/30 ring-1 ring-orange-600/30 hover:from-orange-600 hover:to-orange-700 hover:shadow-xl hover:shadow-orange-500/40 hover:-translate-y-0.5 active:translate-y-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-400 focus-visible:ring-offset-2 transition-all duration-200"
                        data-testid="cta-start-button"
                      >
                        {user && hasPaidAccess ? 'Moje testy' : 'Začať prípravu na T9'}
                      </button>
                      <button
                        onClick={() => navigate('/pricing')}
                        className="flex-1 px-6 py-3.5 bg-white text-blue-700 rounded-xl font-bold text-base border-2 border-blue-600 shadow-sm hover:bg-blue-50 hover:border-blue-700 hover:shadow-md hover:-translate-y-0.5 active:translate-y-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 focus-visible:ring-offset-2 transition-all duration-200"
                        data-testid="cta-pricing-button"
                      >
                        Pozrieť cenník
                      </button>
                    </div>
                    {/* Additional CTA — scrolls to the public "Ukážkové testy zdarma"
                        homepage section so visitors can try a sample test without
                        registering. Does NOT unlock paid tests automatically. */}
                    <button
                      onClick={() => {
                        const el = document.getElementById('ukazkove-testy')
                        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
                      }}
                      className="self-stretch sm:self-center inline-flex items-center justify-center gap-1.5 px-5 py-2.5 bg-emerald-50 text-emerald-700 rounded-xl font-semibold text-sm border border-emerald-200 ring-1 ring-emerald-100 hover:bg-emerald-100 hover:border-emerald-300 hover:ring-emerald-200 hover:-translate-y-0.5 active:translate-y-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400 focus-visible:ring-offset-2 transition-all duration-200"
                      data-testid="cta-free-sample-button"
                    >
                      <svg aria-hidden="true" className="w-4 h-4 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                      </svg>
                      Ukážkový test ZDARMA
                    </button>
                  </div>
                )}

                {/* Decorative illustration — local asset, no baked-in text.
                    Compact on mobile (h-28), balanced on desktop (h-40). */}
                <div
                  className="relative mb-6 rounded-2xl bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-100 ring-1 ring-blue-50 shadow-inner overflow-hidden h-28 sm:h-36 lg:h-40"
                  data-testid="hero-illustration"
                >
                  <img
                    src="/images/t9-student-laptop.png"
                    alt="Študent pri online príprave na Testovanie 9"
                    loading="lazy"
                    decoding="async"
                    className="absolute inset-0 w-full h-full object-contain object-left sm:object-center select-none pointer-events-none"
                    draggable="false"
                  />
                </div>
              </div>

              {/* Price / value box */}
              <div
                className="relative rounded-2xl bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-100 p-4 sm:p-5"
                data-testid="hero-price-box"
              >
                <PromoBanner kind="individual" compact className="mb-3" />
                <div className="flex items-baseline flex-wrap gap-x-2">
                  <span className="text-2xl sm:text-3xl font-extrabold text-blue-700">14,99 €</span>
                  <span className="text-sm sm:text-base text-gray-600 font-medium">/ školský rok</span>
                </div>
                <p className="text-sm text-gray-700 mt-1">
                  Prístup ku všetkým testom, výsledkom a vysvetleniam v jednom balíčku.
                </p>
              </div>
            </div>

            {/* Hero benefits panel (right column on lg+, stacked below on mobile/tablet).
                Real HTML — 4 vertical benefit items on a softer EduMisia blue
                background (medium/deep blue gradient, not near-black navy). */}
            <aside
              className="mt-6 lg:mt-12 lg:self-start rounded-2xl bg-gradient-to-br from-blue-700 via-blue-600 to-indigo-700 text-white shadow-lg shadow-blue-700/20 ring-1 ring-blue-500/30 p-4 sm:p-5 flex flex-col justify-center gap-3 sm:gap-3.5 overflow-hidden relative"
              data-testid="hero-benefit-panel"
              aria-label="Hlavné výhody EduMisia"
            >
              <div aria-hidden="true" className="pointer-events-none absolute -top-16 -right-16 w-40 h-40 rounded-full bg-white/10 blur-3xl" />
              <div aria-hidden="true" className="pointer-events-none absolute -bottom-16 -left-12 w-36 h-36 rounded-full bg-emerald-400/15 blur-3xl" />

              {/* 1. Vysvetlenie každej otázky — emerald lightbulb */}
              <div className="relative flex items-start gap-2.5">
                <span aria-hidden="true" className="flex-shrink-0 inline-flex w-8 h-8 rounded-full bg-emerald-400/25 ring-1 ring-emerald-300/50 text-emerald-100 items-center justify-center">
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 18.75h6m-6 0c0 .75.5 2.25 3 2.25s3-1.5 3-2.25M12 3a6 6 0 00-3.5 10.85V16a1 1 0 001 1h5a1 1 0 001-1v-2.15A6 6 0 0012 3z" />
                  </svg>
                </span>
                <div className="min-w-0">
                  <h3 className="text-[11px] sm:text-xs font-bold uppercase tracking-wide leading-tight text-white">
                    Vysvetlenie každej otázky
                  </h3>
                  <p className="text-[10px] sm:text-[11px] text-blue-100/90 leading-snug mt-0.5">
                    Dieťa pochopí chybu a nezopakuje ju.
                  </p>
                </div>
              </div>

              {/* 2. Testy každé 3 týždne — sky calendar */}
              <div className="relative flex items-start gap-2.5">
                <span aria-hidden="true" className="flex-shrink-0 inline-flex w-8 h-8 rounded-full bg-sky-400/25 ring-1 ring-sky-300/50 text-sky-100 items-center justify-center">
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </span>
                <div className="min-w-0">
                  <h3 className="text-[11px] sm:text-xs font-bold uppercase tracking-wide leading-tight text-white">
                    Testy každé 3 týždne
                  </h3>
                  <p className="text-[10px] sm:text-[11px] text-blue-100/90 leading-snug mt-0.5">
                    Pravidelná príprava, ktorá prináša výsledky.
                  </p>
                </div>
              </div>

              {/* 3. Okamžité výsledky — orange clock */}
              <div className="relative flex items-start gap-2.5">
                <span aria-hidden="true" className="flex-shrink-0 inline-flex w-8 h-8 rounded-full bg-orange-400/25 ring-1 ring-orange-300/50 text-orange-100 items-center justify-center">
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </span>
                <div className="min-w-0">
                  <h3 className="text-[11px] sm:text-xs font-bold uppercase tracking-wide leading-tight text-white">
                    Okamžité výsledky
                  </h3>
                  <p className="text-[10px] sm:text-[11px] text-blue-100/90 leading-snug mt-0.5">
                    Rodič aj žiak vidia pokrok ihneď.
                  </p>
                </div>
              </div>

              {/* 4. Reálna príprava na Testovanie 9 — purple target */}
              <div className="relative flex items-start gap-2.5">
                <span aria-hidden="true" className="flex-shrink-0 inline-flex w-8 h-8 rounded-full bg-purple-400/25 ring-1 ring-purple-300/50 text-purple-100 items-center justify-center">
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <circle cx="12" cy="12" r="9" strokeWidth={2} />
                    <circle cx="12" cy="12" r="5" strokeWidth={2} />
                    <circle cx="12" cy="12" r="1.5" fill="currentColor" stroke="none" />
                  </svg>
                </span>
                <div className="min-w-0">
                  <h3 className="text-[11px] sm:text-xs font-bold uppercase tracking-wide leading-tight text-white">
                    Reálna príprava na Testovanie 9
                  </h3>
                  <p className="text-[10px] sm:text-[11px] text-blue-100/90 leading-snug mt-0.5">
                    Úlohy podobné skutočnému Monitoru.
                  </p>
                </div>
              </div>
            </aside>

            {/* Card 2: Financial Literacy — real HTML (no baked-in image text).
                Temporarily hidden via SHOW_FG_TEASER flag. */}
            {SHOW_FG_TEASER && (
            <div
              className="relative rounded-3xl shadow-2xl bg-white p-8 md:p-10 flex flex-col justify-between animate-fadeIn overflow-hidden"
              data-testid="fg-card"
            >
              {/* Decorative-only background accent */}
              <div
                aria-hidden="true"
                className="pointer-events-none absolute -top-12 -right-12 w-56 h-56 rounded-full bg-green-100 opacity-60 blur-3xl"
              />

              <div className="relative">
                {/* Badge */}
                <div className="mb-3">
                  <span className="inline-block px-3 py-1 bg-green-100 text-green-700 text-xs font-semibold rounded-full">
                    Novinka
                  </span>
                </div>

                {/* Decorative FG illustration — local asset, no baked-in text. */}
                <div
                  className="relative mb-5 rounded-2xl bg-gradient-to-br from-green-50 to-emerald-50 border border-green-100 overflow-hidden h-28 sm:h-36 lg:h-40"
                  data-testid="fg-illustration"
                >
                  <img
                    src="/images/financial-literacy.png"
                    alt="Ilustrácia k finančnej gramotnosti"
                    loading="lazy"
                    decoding="async"
                    className="absolute inset-0 w-full h-full object-contain object-right sm:object-center select-none pointer-events-none"
                    draggable="false"
                  />
                </div>

                <h2
                  className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-gray-900 leading-tight mb-4"
                  data-testid="fg-headline"
                >
                  Finančná gramotnosť pre školy
                </h2>
                <p
                  className="text-base sm:text-lg text-gray-700 leading-relaxed mb-6"
                  data-testid="fg-subheadline"
                >
                  Interaktívne scenáre, rozhodovacie úlohy a praktické aktivity pre žiakov 5. – 9. ročníka.
                </p>

                {/* Value badges */}
                <ul className="flex flex-wrap gap-2 mb-6" data-testid="fg-badges">
                  <li className="inline-flex items-center px-3 py-1 rounded-full bg-green-50 text-green-700 text-xs sm:text-sm font-medium border border-green-100">
                    Interaktívne lekcie
                  </li>
                  <li className="inline-flex items-center px-3 py-1 rounded-full bg-green-50 text-green-700 text-xs sm:text-sm font-medium border border-green-100">
                    Praktické úlohy
                  </li>
                  <li className="inline-flex items-center px-3 py-1 rounded-full bg-green-50 text-green-700 text-xs sm:text-sm font-medium border border-green-100">
                    Pre 5. – 9. ročník
                  </li>
                </ul>

                {/* CTA — Financial Literacy is not yet available; button is a disabled placeholder. */}
                <div className="flex flex-col sm:flex-row gap-3 mb-6">
                  <button
                    type="button"
                    disabled
                    aria-disabled="true"
                    title="Finančná gramotnosť zatiaľ nie je dostupná"
                    className="flex-1 px-6 py-3 bg-gray-200 text-gray-500 rounded-xl font-bold text-base cursor-not-allowed select-none"
                    data-testid="fg-cta-button"
                  >
                    Pripravujeme
                  </button>
                </div>
              </div>

              {/* Price / value box */}
              <div
                className="relative rounded-2xl bg-gradient-to-br from-green-50 to-emerald-50 border border-green-100 p-4 sm:p-5"
                data-testid="fg-price-box"
              >
                <div className="flex items-baseline flex-wrap gap-x-2">
                  <span className="text-2xl sm:text-3xl font-extrabold text-green-700">250 €</span>
                  <span className="text-sm sm:text-base text-gray-600 font-medium">/ školský rok</span>
                </div>
                <p className="text-sm text-gray-700 mt-1">
                  Ročný prístup pre celú školu k programu finančnej gramotnosti.
                </p>
              </div>
            </div>
            )}

          </div>

          </div>{/* /unified hero surface */}

          {/* Public social-proof: registered users counter. Sits between the
              hero grid and the parent trust band — close to the main message
              but visually lighter than the hero CTA. */}
          <RegisteredUsersCounter />

          {/* Parent trust band — compact reassurance row, parent-facing.
              Sits between the hero grid and the deeper benefit sections. */}
          <section
            className="mt-6 md:mt-8 animate-fadeIn"
            data-testid="parent-trust-band"
            aria-label="Hodnoty pre rodičov"
          >
            <div className="max-w-5xl mx-auto rounded-2xl bg-gradient-to-r from-emerald-50/60 via-white to-blue-50/60 border border-emerald-100 ring-1 ring-emerald-50 shadow-sm p-4 sm:p-5">
              <div className="text-center mb-3 sm:mb-4">
                <p className="text-sm sm:text-base font-semibold text-gray-900">
                  Menej stresu pre rodičov. Viac istoty pre deti.
                </p>
              </div>
              <ul
                className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-3"
                data-testid="trust-points"
              >
                <li className="flex items-center gap-2 sm:gap-3 rounded-xl bg-white border border-gray-100 ring-1 ring-gray-50 px-3 py-2.5 sm:px-4 sm:py-3">
                  <span className="flex-shrink-0 w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-gradient-to-br from-emerald-500 to-green-600 text-white flex items-center justify-center shadow-sm">
                    <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                    </svg>
                  </span>
                  <span className="text-xs sm:text-sm font-medium text-gray-800 leading-snug">Dieťa chápe chyby</span>
                </li>
                <li className="flex items-center gap-2 sm:gap-3 rounded-xl bg-white border border-gray-100 ring-1 ring-gray-50 px-3 py-2.5 sm:px-4 sm:py-3">
                  <span className="flex-shrink-0 w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-gradient-to-br from-blue-600 to-indigo-600 text-white flex items-center justify-center shadow-sm">
                    <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6h13M9 6l-3 3m3-3l-3-3M5 13H3m2 0v6m0-6V7" />
                    </svg>
                  </span>
                  <span className="text-xs sm:text-sm font-medium text-gray-800 leading-snug">Vidíte pokrok</span>
                </li>
                <li className="flex items-center gap-2 sm:gap-3 rounded-xl bg-white border border-gray-100 ring-1 ring-gray-50 px-3 py-2.5 sm:px-4 sm:py-3">
                  <span className="flex-shrink-0 w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-gradient-to-br from-amber-500 to-orange-500 text-white flex items-center justify-center shadow-sm">
                    <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </span>
                  <span className="text-xs sm:text-sm font-medium text-gray-800 leading-snug">Pravidelná príprava</span>
                </li>
                <li className="flex items-center gap-2 sm:gap-3 rounded-xl bg-white border border-gray-100 ring-1 ring-gray-50 px-3 py-2.5 sm:px-4 sm:py-3">
                  <span className="flex-shrink-0 w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-gradient-to-br from-emerald-500 to-green-600 text-white flex items-center justify-center shadow-sm">
                    <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                    </svg>
                  </span>
                  <span className="text-xs sm:text-sm font-medium text-gray-800 leading-snug">Okamžité výsledky</span>
                </li>
              </ul>
            </div>
          </section>

          {/* "Ukážkové testy zdarma" — public, no-auth sample test cards.
              Lets visitors experience the product (a real graded test +
              explanations) before deciding to register. Only the
              backend-whitelisted sample tests are exposed. */}
          {sampleTests.length > 0 && (
            <section
              id="ukazkove-testy"
              className="mt-8 md:mt-12 scroll-mt-24"
              data-testid="ukazkove-testy-section"
              aria-label="Ukážkové testy zdarma"
            >
              <div className="max-w-5xl mx-auto">
                <header className="text-center mb-5 sm:mb-6">
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-50 text-emerald-700 text-xs font-bold uppercase tracking-wider ring-1 ring-emerald-200">
                    Bez registrácie
                  </span>
                  <h2 className="mt-3 text-2xl sm:text-3xl md:text-4xl font-extrabold text-slate-900">
                    Ukážkové testy zdarma
                  </h2>
                  <p className="mt-2 text-sm sm:text-base text-slate-800 font-medium max-w-2xl mx-auto">
                    Vyskúšajte si, ako EduMisia funguje – bez registrácie a bez platby.
                  </p>
                </header>

                <div className="grid sm:grid-cols-2 gap-4 sm:gap-5" data-testid="sample-tests-grid">
                  {sampleTests.map((st, idx) => (
                    <div
                      key={st.id}
                      className="group relative rounded-3xl bg-white ring-1 ring-slate-200 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 p-5 sm:p-6 flex flex-col"
                      data-testid={`sample-test-card-${idx + 1}`}
                    >
                      <div className="flex items-center justify-between mb-3">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full bg-blue-50 text-blue-700 text-[11px] font-semibold ring-1 ring-blue-100 capitalize">
                          {st.subject || 'Test'}
                        </span>
                        <span className="text-[11px] font-semibold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full ring-1 ring-emerald-100">
                          ZDARMA
                        </span>
                      </div>
                      <h3 className="text-lg sm:text-xl font-bold text-slate-900 leading-snug mb-1.5">
                        {st.title}
                      </h3>
                      {st.description && (
                        <p className="text-sm text-slate-600 leading-snug mb-4 line-clamp-3">
                          {st.description}
                        </p>
                      )}
                      <div className="mt-auto pt-2 flex items-center justify-between gap-3">
                        <div className="text-xs text-slate-500">
                          {st.question_count} otázok · okamžitý výsledok
                        </div>
                        <button
                          onClick={() => navigate(`/sample-test/${st.id}`)}
                          className="inline-flex items-center justify-center gap-1.5 px-4 py-2 bg-gradient-to-b from-emerald-500 to-emerald-600 text-white rounded-xl font-bold text-sm shadow-md shadow-emerald-500/25 hover:from-emerald-600 hover:to-emerald-700 hover:shadow-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400 focus-visible:ring-offset-2 transition-all duration-200"
                          data-testid={`sample-test-start-${idx + 1}`}
                        >
                          Spustiť ukážkový test
                          <svg aria-hidden="true" className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
                <p className="mt-4 text-center text-xs text-slate-700 font-medium">
                  Tvoje odpovede v ukážkovom teste sa neukladajú a nevytvárajú žiadny účet.
                </p>
              </div>
            </section>
          )}

          {/* Main Value Section - "Prečo EduMisia?" */}
          <div id="preco-t9" className="mt-8 md:mt-12 scroll-mt-20">
            <div className="max-w-4xl mx-auto bg-gradient-to-b from-white via-[#F4FBF2] to-[#EAF7E6] rounded-2xl shadow-lg ring-1 ring-slate-100 p-6 sm:p-8 md:p-12">
              <header className="mb-6 md:mb-8 text-center">
                <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-3">
                  Prečo EduMisia?
                </h2>
                <p className="text-sm sm:text-base text-gray-600 leading-relaxed max-w-2xl mx-auto">
                  Pomáha deťom pripravovať sa pravidelne, pokojnejšie a s lepším pochopením chýb.
                </p>
              </header>

              <ul className="grid sm:grid-cols-2 gap-3 sm:gap-4" data-testid="preco-benefits">
                <li className="flex items-start gap-3 sm:gap-4 rounded-2xl bg-white border border-gray-100 ring-1 ring-gray-50 shadow-sm hover:shadow-md transition-shadow p-4 sm:p-5">
                  <span className="flex-shrink-0 w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-gradient-to-br from-emerald-500 to-green-600 text-white flex items-center justify-center shadow-md shadow-emerald-500/25 ring-4 ring-emerald-50">
                    <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                    </svg>
                  </span>
                  <div>
                    <h3 className="font-semibold text-gray-900 text-sm sm:text-base mb-1">
                      Pravidelný systém prípravy
                    </h3>
                    <p className="text-gray-600 text-sm leading-snug">
                      Testy každé 3 týždne. O novom teste vás upozorníme e-mailom hneď po jeho zverejnení.
                    </p>
                  </div>
                </li>

                <li className="flex items-start gap-3 sm:gap-4 rounded-2xl bg-white border border-gray-100 ring-1 ring-gray-50 shadow-sm hover:shadow-md transition-shadow p-4 sm:p-5">
                  <span className="flex-shrink-0 w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-gradient-to-br from-emerald-500 to-green-600 text-white flex items-center justify-center shadow-md shadow-emerald-500/25 ring-4 ring-emerald-50">
                    <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                    </svg>
                  </span>
                  <div>
                    <h3 className="font-semibold text-gray-900 text-sm sm:text-base mb-1">
                      Menej neistoty pred testovaním
                    </h3>
                    <p className="text-gray-600 text-sm leading-snug">
                      Dieťa si priebežne overuje vedomosti a získava väčšiu istotu.
                    </p>
                  </div>
                </li>

                <li className="flex items-start gap-3 sm:gap-4 rounded-2xl bg-white border border-gray-100 ring-1 ring-gray-50 shadow-sm hover:shadow-md transition-shadow p-4 sm:p-5">
                  <span className="flex-shrink-0 w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-gradient-to-br from-emerald-500 to-green-600 text-white flex items-center justify-center shadow-md shadow-emerald-500/25 ring-4 ring-emerald-50">
                    <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                    </svg>
                  </span>
                  <div>
                    <h3 className="font-semibold text-gray-900 text-sm sm:text-base mb-1">
                      Vysvetlené odpovede
                    </h3>
                    <p className="text-gray-600 text-sm leading-snug">
                      Po teste vidí správne riešenie aj jednoduché vysvetlenie.
                    </p>
                  </div>
                </li>

                <li className="flex items-start gap-3 sm:gap-4 rounded-2xl bg-white border border-gray-100 ring-1 ring-gray-50 shadow-sm hover:shadow-md transition-shadow p-4 sm:p-5">
                  <span className="flex-shrink-0 w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-gradient-to-br from-emerald-500 to-green-600 text-white flex items-center justify-center shadow-md shadow-emerald-500/25 ring-4 ring-emerald-50">
                    <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                    </svg>
                  </span>
                  <div>
                    <h3 className="font-semibold text-gray-900 text-sm sm:text-base mb-1">
                      Prehľad pre rodiča
                    </h3>
                    <p className="text-gray-600 text-sm leading-snug">
                      Výsledky a pokrok sú dostupné na jednom mieste.
                    </p>
                  </div>
                </li>
              </ul>

              <div className="mt-8 pt-6 border-t border-gray-200 text-center">
                <button
                  onClick={() => navigate('/pricing')}
                  className="inline-block px-6 sm:px-8 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition"
                  data-testid="preco-cta-button"
                >
                  Pozrieť ceny a začať
                </button>
              </div>
            </div>
          </div>

          {/* "Čo ponúkame?" Benefits */}
          <section id="co-ponukame" className="mt-8 md:mt-12 scroll-mt-20">
            <header className="mb-6 md:mb-8 text-center">
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-3">
                Čo ponúkame?
              </h2>
              <p className="text-sm sm:text-base text-gray-600 leading-relaxed max-w-2xl mx-auto">
                Všetko dôležité pre priebežnú prípravu na Testovanie 9 na jednom mieste.
              </p>
            </header>

            <ul className="grid sm:grid-cols-2 gap-3 sm:gap-4" data-testid="ponukame-benefits">
              <li
                className="flex items-start gap-3 sm:gap-4 rounded-2xl bg-white border border-gray-100 ring-1 ring-gray-50 shadow-sm hover:shadow-md transition-shadow p-4 sm:p-5"
                data-testid="benefit-monthly-tests"
              >
                <span className="flex-shrink-0 w-9 h-9 rounded-lg bg-blue-100 text-blue-600 flex items-center justify-center">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </span>
                <div className="min-w-0">
                  <h3 className="font-semibold text-gray-900 text-sm sm:text-base mb-1">
                    Online testy počas školského roka
                  </h3>
                  <p className="text-gray-600 text-sm leading-snug">
                    Testy sa sprístupňujú postupne podľa harmonogramu.
                  </p>
                </div>
              </li>

              <li
                className="flex items-start gap-3 sm:gap-4 rounded-2xl bg-white border border-gray-100 ring-1 ring-gray-50 shadow-sm hover:shadow-md transition-shadow p-4 sm:p-5"
                data-testid="benefit-instant-results"
              >
                <span className="flex-shrink-0 w-9 h-9 rounded-lg bg-green-100 text-green-600 flex items-center justify-center">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </span>
                <div className="min-w-0">
                  <h3 className="font-semibold text-gray-900 text-sm sm:text-base mb-1">
                    Okamžité vyhodnotenie
                  </h3>
                  <p className="text-gray-600 text-sm leading-snug">
                    Po dokončení testu dieťa hneď vidí výsledok, percentá a správne odpovede.
                  </p>
                </div>
              </li>

              {/* Highlighted key value: vysvetlenia (same card system, stronger blue ring) */}
              <li
                className="flex items-start gap-3 sm:gap-4 rounded-2xl bg-white border border-blue-100 ring-2 ring-blue-200 shadow-sm hover:shadow-md transition-shadow p-4 sm:p-5 sm:col-span-2 relative"
                data-testid="benefit-explanations"
              >
                <span className="absolute top-2 right-3 inline-block px-2 py-0.5 rounded-full bg-blue-600 text-white text-[10px] font-semibold uppercase tracking-wide">
                  Kľúčová hodnota
                </span>
                <span className="flex-shrink-0 w-9 h-9 rounded-lg bg-blue-600 text-white flex items-center justify-center">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                  </svg>
                </span>
                <div className="min-w-0 pr-24 sm:pr-28">
                  <h3 className="font-semibold text-gray-900 text-sm sm:text-base mb-1">
                    Vysvetlenia k otázkam
                  </h3>
                  <p className="text-gray-700 text-sm leading-snug">
                    Ku každej otázke nájde nápovedu alebo jednoduché vysvetlenie správnej odpovede.
                  </p>
                </div>
              </li>

              <li
                className="flex items-start gap-3 sm:gap-4 rounded-2xl bg-white border border-gray-100 ring-1 ring-gray-50 shadow-sm hover:shadow-md transition-shadow p-4 sm:p-5"
                data-testid="benefit-progress"
              >
                <span className="flex-shrink-0 w-9 h-9 rounded-lg bg-emerald-100 text-emerald-700 flex items-center justify-center">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                </span>
                <div className="min-w-0">
                  <h3 className="font-semibold text-gray-900 text-sm sm:text-base mb-1">
                    Prehľad pokroku
                  </h3>
                  <p className="text-gray-600 text-sm leading-snug">
                    Výsledky pomáhajú rodičovi aj dieťaťu vidieť, čo už ide dobre a kde sa ešte zlepšiť.
                  </p>
                </div>
              </li>

              <li
                className="flex items-start gap-3 sm:gap-4 rounded-2xl bg-white border border-gray-100 ring-1 ring-gray-50 shadow-sm hover:shadow-md transition-shadow p-4 sm:p-5"
                data-testid="benefit-access-until-may"
              >
                <span className="flex-shrink-0 w-9 h-9 rounded-lg bg-orange-100 text-orange-600 flex items-center justify-center">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </span>
                <div className="min-w-0">
                  <h3 className="font-semibold text-gray-900 text-sm sm:text-base mb-1">
                    Prístup do 31. mája
                  </h3>
                  <p className="text-gray-600 text-sm leading-snug">
                    K zverejneným testom, výsledkom a vysvetleniam sa môžete vracať až do 31. mája daného školského roka.
                  </p>
                </div>
              </li>
            </ul>

            <p className="mt-6 text-center text-sm text-gray-600" data-testid="ponukame-price-note">
              Celý balíček je dostupný za <span className="font-semibold text-blue-700">14,99 € / školský rok</span>.
            </p>
          </section>

          {/* Ukážka vysvetlenia otázky — static preview block.
              Demonstrates the strongest product benefit (the child sees WHY
              an answer is correct), but is not connected to backend in any
              way and renders no real test data. */}
          <section
            id="ukazka-vysvetlenia"
            className="mt-8 md:mt-12 scroll-mt-20"
            data-testid="explanation-preview-section"
            aria-labelledby="ukazka-vysvetlenia-heading"
          >
            <header className="mb-6 md:mb-8 text-center">
              <h2
                id="ukazka-vysvetlenia-heading"
                className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-3"
              >
                Ukážka vysvetlenia otázky
              </h2>
              <p className="text-sm sm:text-base text-gray-800 font-medium leading-relaxed max-w-2xl mx-auto">
                Takto vyzerá vyhodnotenie jednej otázky v EduMisii — dieťa hneď vidí, prečo je daná odpoveď správna.
              </p>
            </header>

            <div
              className="max-w-3xl mx-auto rounded-2xl bg-slate-50 border border-slate-200 shadow-sm ring-1 ring-slate-100 overflow-hidden"
              data-testid="explanation-preview-card"
              aria-label="Statická ukážka vyhodnotenia otázky"
            >
              {/* Meta row */}
              <div className="flex items-center justify-between gap-3 px-5 sm:px-6 py-3 bg-gray-50 border-b border-gray-100">
                <div className="min-w-0 flex items-center gap-2 text-xs sm:text-sm text-gray-600">
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-white border border-gray-200 text-gray-700 font-medium">
                    Matematika
                  </span>
                  <span className="truncate">Otázka 3 z 20</span>
                </div>
                <span className="inline-flex items-center gap-1 text-xs sm:text-sm text-green-700 font-semibold">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Vyhodnotené
                </span>
              </div>

              {/* Question */}
              <div className="px-5 sm:px-6 py-5">
                <p className="text-xs sm:text-sm text-gray-500 mb-2">Otázka</p>
                <h3 className="text-base sm:text-lg font-semibold text-gray-900 leading-snug">
                  Koľko je 25 % zo 60?
                </h3>
              </div>

              {/* Options */}
              <ul className="px-5 sm:px-6 pb-3 space-y-2" data-testid="explanation-preview-options">
                <li className="flex items-center gap-3 rounded-xl border border-gray-200 bg-white px-4 py-3">
                  <span className="flex-shrink-0 w-7 h-7 rounded-full border border-gray-300 text-gray-500 text-sm font-semibold flex items-center justify-center">A</span>
                  <span className="text-sm sm:text-base text-gray-700">10</span>
                </li>
                <li
                  className="flex items-center gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3"
                  data-testid="explanation-preview-selected"
                >
                  <span className="flex-shrink-0 w-7 h-7 rounded-full bg-red-600 text-white text-sm font-semibold flex items-center justify-center">B</span>
                  <span className="text-sm sm:text-base text-gray-800">12</span>
                  <span className="ml-auto text-xs sm:text-sm text-red-700 font-medium whitespace-nowrap">Zvolená</span>
                </li>
                <li
                  className="flex items-center gap-3 rounded-xl border-2 border-green-300 bg-green-50 px-4 py-3"
                  data-testid="explanation-preview-correct"
                >
                  <span className="flex-shrink-0 w-7 h-7 rounded-full bg-green-600 text-white text-sm font-semibold flex items-center justify-center">C</span>
                  <span className="text-sm sm:text-base text-gray-900 font-medium">15</span>
                  <span className="ml-auto text-xs sm:text-sm text-green-700 font-semibold whitespace-nowrap">Správne</span>
                </li>
                <li className="flex items-center gap-3 rounded-xl border border-gray-200 bg-white px-4 py-3">
                  <span className="flex-shrink-0 w-7 h-7 rounded-full border border-gray-300 text-gray-500 text-sm font-semibold flex items-center justify-center">D</span>
                  <span className="text-sm sm:text-base text-gray-700">20</span>
                </li>
              </ul>

              {/* Explanation */}
              <div className="mx-5 sm:mx-6 mb-5 sm:mb-6 mt-3 rounded-xl bg-blue-50 border border-blue-100 p-4 sm:p-5">
                <div className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-8 h-8 rounded-lg bg-blue-600 text-white flex items-center justify-center">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                    </svg>
                  </span>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-blue-900 mb-1">Vysvetlenie</p>
                    <p className="text-sm text-blue-900/90 leading-relaxed" data-testid="explanation-preview-text">
                      25 % znamená štvrtinu. Štvrtinu zo 60 vypočítaš jednoducho: 60 ÷ 4 = <strong>15</strong>. Ak si najprv zistíš 10 % (teda 6), potom 25 % je dve a pol násobok desiatich percent — opäť <strong>15</strong>.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <p className="mt-4 text-center text-xs sm:text-sm text-gray-700 font-medium" data-testid="explanation-preview-note">
              Ukážka je iba ilustračná. V EduMisii sa vysvetlenie k odpovedi zobrazuje po dokončení každej otázky.
            </p>
          </section>
        </div>
      </div>

      {/* How it works */}
      <div id="ako-funguje" className="bg-[#F4FBF2]/60 py-10 md:py-14 scroll-mt-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <header className="mb-6 md:mb-8 text-center">
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-3">
                Ako to funguje?
              </h2>
              <p className="text-sm sm:text-base text-gray-600 leading-relaxed max-w-2xl mx-auto">
                Päť jednoduchých krokov od registrácie po kľudnejšiu prípravu.
              </p>
            </header>

            <ol className="space-y-3 sm:space-y-4" data-testid="ako-steps">
              <li className="flex items-start gap-3 sm:gap-4 rounded-2xl bg-white border border-gray-100 ring-1 ring-gray-50 shadow-sm hover:shadow-md transition-shadow p-4 sm:p-5">
                <span className="flex-shrink-0 w-10 h-10 sm:w-11 sm:h-11 bg-gradient-to-br from-blue-600 to-indigo-600 text-white rounded-full flex items-center justify-center font-bold text-base sm:text-lg shadow-md shadow-blue-500/30 ring-4 ring-blue-50">
                  1
                </span>
                <div className="min-w-0">
                  <h3 className="font-semibold text-gray-900 text-base sm:text-lg mb-1">
                    Zaregistrujete sa a získate prístup
                  </h3>
                  <p className="text-gray-600 text-sm leading-snug">
                    Po registrácii môžete začať s dostupnými testami. Plný balíček odomkne všetky zverejnené testy, výsledky a vysvetlenia.
                  </p>
                </div>
              </li>

              <li className="flex items-start gap-3 sm:gap-4 rounded-2xl bg-white border border-gray-100 ring-1 ring-gray-50 shadow-sm hover:shadow-md transition-shadow p-4 sm:p-5">
                <span className="flex-shrink-0 w-10 h-10 sm:w-11 sm:h-11 bg-gradient-to-br from-blue-600 to-indigo-600 text-white rounded-full flex items-center justify-center font-bold text-base sm:text-lg shadow-md shadow-blue-500/30 ring-4 ring-blue-50">
                  2
                </span>
                <div className="min-w-0">
                  <h3 className="font-semibold text-gray-900 text-base sm:text-lg mb-1">
                    Testy riešite podľa harmonogramu
                  </h3>
                  <p className="text-gray-600 text-sm leading-snug">
                    Nové testy sa sprístupňujú postupne počas školského roka.
                  </p>
                </div>
              </li>

              <li className="flex items-start gap-3 sm:gap-4 rounded-2xl bg-white border border-gray-100 ring-1 ring-gray-50 shadow-sm hover:shadow-md transition-shadow p-4 sm:p-5">
                <span className="flex-shrink-0 w-10 h-10 sm:w-11 sm:h-11 bg-gradient-to-br from-blue-600 to-indigo-600 text-white rounded-full flex items-center justify-center font-bold text-base sm:text-lg shadow-md shadow-blue-500/30 ring-4 ring-blue-50">
                  3
                </span>
                <div className="min-w-0">
                  <h3 className="font-semibold text-gray-900 text-base sm:text-lg mb-1">
                    Výsledok vidíte hneď po teste
                  </h3>
                  <p className="text-gray-600 text-sm leading-snug">
                    Po dokončení testu dieťa uvidí svoje výsledky, percentá a správne odpovede.
                  </p>
                </div>
              </li>

              <li className="flex items-start gap-3 sm:gap-4 rounded-2xl bg-white border border-gray-100 ring-1 ring-gray-50 shadow-sm hover:shadow-md transition-shadow p-4 sm:p-5">
                <span className="flex-shrink-0 w-10 h-10 sm:w-11 sm:h-11 bg-gradient-to-br from-blue-600 to-indigo-600 text-white rounded-full flex items-center justify-center font-bold text-base sm:text-lg shadow-md shadow-blue-500/30 ring-4 ring-blue-50">
                  4
                </span>
                <div className="min-w-0">
                  <h3 className="font-semibold text-gray-900 text-base sm:text-lg mb-1">
                    Učí sa z vysvetlení
                  </h3>
                  <p className="text-gray-600 text-sm leading-snug">
                    Ku každej otázke nájde nápovedu alebo jednoduché vysvetlenie správnej odpovede.
                  </p>
                </div>
              </li>

              <li className="flex items-start gap-3 sm:gap-4 rounded-2xl bg-white border border-gray-100 ring-1 ring-gray-50 shadow-sm hover:shadow-md transition-shadow p-4 sm:p-5">
                <span className="flex-shrink-0 w-10 h-10 sm:w-11 sm:h-11 bg-gradient-to-br from-blue-600 to-indigo-600 text-white rounded-full flex items-center justify-center font-bold text-base sm:text-lg shadow-md shadow-blue-500/30 ring-4 ring-blue-50">
                  5
                </span>
                <div className="min-w-0">
                  <h3 className="font-semibold text-gray-900 text-base sm:text-lg mb-1">
                    Prístup máte do 31. mája
                  </h3>
                  <p className="text-gray-600 text-sm leading-snug">
                    K testom, výsledkom a vysvetleniam sa môžete vracať až do 31. mája daného školského roka.
                  </p>
                </div>
              </li>
            </ol>
          </div>
        </div>
      </div>

      {/* Pricing / Cenník Section */}
      <div id="cennik" className="bg-gradient-to-b from-white via-[#F4FBF2] to-[#EAF7E6] py-10 md:py-14 scroll-mt-20">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <header className="mb-6 md:mb-8 text-center">
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-3">
                Cenník
              </h2>
              <p className="text-sm sm:text-base text-gray-600 leading-relaxed max-w-2xl mx-auto">
                Jednoduchý balíček pre priebežnú prípravu na Testovanie 9.
              </p>
            </header>

            {/* Decorative support visual — local asset, no baked-in text.
                Sits above the pricing card and does not replace any CTA. */}
            <div
              className="hidden sm:block max-w-2xl mx-auto mb-6 md:mb-8 rounded-2xl bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-100 overflow-hidden h-32 md:h-40"
              data-testid="cennik-illustration"
            >
              <img
                src="/images/t9-purchase-visual.png"
                alt="Online príprava na Testovanie 9"
                loading="lazy"
                decoding="async"
                className="w-full h-full object-contain object-center select-none pointer-events-none"
                draggable="false"
              />
            </div>

            <div
              className="relative bg-gradient-to-br from-slate-100 via-slate-50 to-blue-50/60 rounded-2xl shadow-lg border border-slate-200 ring-1 ring-slate-100 overflow-hidden"
              data-testid="cennik-card"
            >
              <div className="p-6 sm:p-8 md:p-10">
                <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-4">
                  EduMisia – školský rok
                </h3>

                <PromoBanner kind="individual" className="mb-5" />

                <div className="flex items-baseline flex-wrap gap-x-2 mb-2" data-testid="cennik-price">
                  <span className="text-4xl sm:text-5xl font-extrabold text-blue-700">
                    14,99 €
                  </span>
                  <span className="text-sm sm:text-base text-gray-600 font-medium">
                    / školský rok
                  </span>
                </div>
                <p className="text-sm text-gray-600 mb-6">
                  Prístup ku všetkým zverejneným testom, výsledkom a vysvetleniam v jednom balíčku.
                </p>

                <ul className="space-y-2 mb-6" data-testid="cennik-included">
                  {[
                    'Online testy sprístupňované počas školského roka',
                    'Okamžité vyhodnotenie',
                    'Vysvetlenia k otázkam',
                    'Prehľad výsledkov a pokroku',
                    'Prístup do 31. mája',
                  ].map((item) => (
                    <li key={item} className="flex items-start gap-3 text-sm sm:text-base text-gray-700">
                      <span className="flex-shrink-0 mt-0.5 w-5 h-5 rounded-full bg-green-100 text-green-600 inline-flex items-center justify-center">
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                        </svg>
                      </span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>

                <button
                  onClick={() => navigate(user && hasPaidAccess ? '/dashboard' : '/pricing')}
                  className="w-full px-6 py-3 bg-orange-500 text-white rounded-xl font-bold text-base hover:bg-orange-600 transition-all duration-200 shadow-md"
                  data-testid="cennik-cta-button"
                >
                  {user && hasPaidAccess ? 'Moje testy' : 'Začať prípravu na T9'}
                </button>

                <p
                  className="mt-4 text-xs sm:text-sm text-gray-600 leading-snug text-center"
                  data-testid="cennik-legal-notice"
                >
                  Balíček môže zakúpiť iba osoba staršia ako 18 rokov, alebo rodič či iný zákonný zástupca maloletého používateľa.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* FAQ Section */}
      <div id="faq" className="bg-emerald-50/25 py-10 md:py-14 scroll-mt-20">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <header className="mb-6 md:mb-8 text-center">
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-3">
                Často kladené otázky
              </h2>
              <p className="text-sm sm:text-base text-gray-600 leading-relaxed max-w-2xl mx-auto">
                Stručné odpovede na najčastejšie otázky rodičov a žiakov.
              </p>
            </header>

            <div className="space-y-3" data-testid="faq-list">
              {[
                {
                  q: 'Pre koho je EduMisia určená?',
                  a: 'EduMisia je určená najmä pre žiakov 9. ročníka, ktorí sa pripravujú na Testovanie 9, a pre rodičov, ktorí chcú mať lepší prehľad o príprave.',
                },
                {
                  q: 'Čo získam v balíčku?',
                  a: 'Balíček obsahuje prístup k zverejneným online testom, okamžitým výsledkom, správnym odpovediam a vysvetleniam k otázkam.',
                },
                {
                  q: 'Ako sa testy sprístupňujú?',
                  a: 'Testy sa sprístupňujú postupne počas školského roka podľa harmonogramu.',
                },
                {
                  q: 'Dokedy platí prístup?',
                  a: 'Prístup k testom, výsledkom a vysvetleniam platí do 31. mája daného školského roka.',
                },
                {
                  q: 'Koľko stojí prístup?',
                  a: 'Prístup stojí 14,99 € / školský rok.',
                },
                {
                  q: 'Kto môže balíček zakúpiť?',
                  a: 'Balíček môže zakúpiť iba osoba staršia ako 18 rokov, alebo rodič či iný zákonný zástupca maloletého používateľa.',
                },
                {
                  q: 'Sú odpovede po teste vysvetlené?',
                  a: 'Áno. Pri otázkach sú dostupné nápovedy alebo jednoduché vysvetlenia, aby dieťa lepšie pochopilo, kde spravilo chybu.',
                },
                {
                  q: 'Je EduMisia oficiálnym testom Testovanie 9?',
                  a: 'Nie. EduMisia je vzdelávacia platforma na prípravu. Nie je oficiálnym testom Testovanie 9.',
                },
              ].map(({ q, a }) => (
                <details
                  key={q}
                  className="group rounded-xl bg-gray-50 border border-gray-100 open:bg-white open:border-blue-200 open:shadow-sm transition-colors"
                  data-testid={`faq-item-${q.slice(0, 20).toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                >
                  <summary className="cursor-pointer list-none flex items-start justify-between gap-3 p-4 sm:p-5">
                    <h3 className="font-semibold text-gray-900 text-sm sm:text-base">{q}</h3>
                    <svg
                      className="flex-shrink-0 w-5 h-5 text-gray-500 transition-transform duration-200 group-open:rotate-180"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      aria-hidden="true"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </summary>
                  <p className="px-4 sm:px-5 pb-4 sm:pb-5 -mt-1 text-sm sm:text-base text-gray-700 leading-snug">
                    {a}
                  </p>
                </details>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* O nás Section */}
      <div id="o-nas" className="bg-emerald-50/25 py-10 md:py-14 scroll-mt-20">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <header className="mb-6 md:mb-8 text-center">
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-3">
                O nás
              </h2>
              <p
                className="text-sm sm:text-base text-gray-600 leading-relaxed max-w-2xl mx-auto"
                data-testid="onas-subheadline"
              >
                EduMisia vznikla preto, aby bola príprava na Testovanie 9 prehľadnejšia, pokojnejšia a užitočnejšia.
              </p>
            </header>

            <div
              className="bg-gray-50 border border-gray-100 rounded-2xl p-6 sm:p-8"
              data-testid="onas-card"
            >
              <p className="text-sm sm:text-base text-gray-700 leading-relaxed mb-4">
                Veríme, že príprava na Testovanie 9 nemá byť len o vyplnení testu. Dôležité je, aby dieťa pochopilo, kde robí chyby, videlo svoj pokrok a vedelo sa pripravovať pravidelne počas školského roka.
              </p>
              <p className="text-sm sm:text-base text-gray-700 leading-relaxed">
                Preto EduMisia spája online testy, okamžité výsledky a jednoduché vysvetlenia odpovedí na jednom mieste. Rodič tak získava lepší prehľad a dieťa viac istoty pred testovaním.
              </p>

              <ul className="mt-6 grid sm:grid-cols-2 gap-2 sm:gap-3" data-testid="onas-bullets">
                {[
                  'prehľadná príprava počas školského roka',
                  'okamžité výsledky po teste',
                  'vysvetlenia k otázkam',
                  'dôraz na pochopenie, nie tipovanie',
                ].map((item) => (
                  <li
                    key={item}
                    className="flex items-start gap-2 text-sm text-gray-700"
                  >
                    <span className="flex-shrink-0 mt-0.5 w-5 h-5 rounded-full bg-green-100 text-green-600 inline-flex items-center justify-center">
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    </span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section - only show for non-paid users (guest or unpaid) */}
      {!accessLoading && !(user && hasPaidAccess) && (
        <div className="bg-blue-600 py-12 md:py-16">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl font-bold text-white mb-6">Pripravení začať?</h2>
            <p className="text-xl text-blue-100 mb-8">Získajte prístup k programu EduMisia už dnes</p>
            <button
              onClick={() => navigate('/pricing')}
              className="px-8 py-4 bg-white text-blue-600 rounded-lg font-semibold text-lg hover:bg-blue-50 transition"
              data-testid="cta-bottom-button"
            >
              Zobraziť ceny a začať
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

export default Home
Home
